import React, { Fragment, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { get, xor } from 'lodash-es';
import { AnimatedCollapse } from 'site-modules/shared/utils/collapse-utils';

import './links-list.scss';

export function LinksList({
  list,
  heading = null,
  keyPrefix,
  headingClassName = null,
  listClassName = null,
  itemClassName = null,
  linkClassName = null,
  subListClassName = null,
  subItemClassName = null,
}) {
  const [openedSections, setOpenedSections] = useState([]);

  const onCollapseClick = useCallback(
    ({ currentTarget: { id } }) => {
      setOpenedSections(xor(openedSections, [id]));
    },
    [openedSections]
  );

  const headingId = `${keyPrefix}_list-heading`;

  return (
    <Fragment>
      {!!heading && (
        <div id={headingId} className={headingClassName}>
          {heading}
        </div>
      )}
      <ul
        className={classnames('links-list list-unstyled', listClassName)}
        aria-labelledby={heading ? headingId : undefined}
        aria-live="polite"
        aria-atomic
      >
        {list.map(({ url, text, highlighted, trackingId, trackingValue, subList }, index) => {
          const key = `${keyPrefix}_list-item_${index}`;
          const collapseId = `${key}_collapse`;
          const isOpen = openedSections.includes(key);
          const ListTag = url ? 'a' : 'button';

          return (
            <li key={key} className={classnames('list-item', itemClassName)}>
              <ListTag
                id={key}
                href={url}
                onClick={onCollapseClick}
                className={classnames('list-link w-100', linkClassName, { highlighted })}
                data-tracking-id={url ? trackingId : `${trackingId}${isOpen ? '_collapse' : '_expand'}`}
                data-tracking-value={trackingValue}
                aria-expanded={url ? undefined : isOpen}
                aria-controls={url ? undefined : collapseId}
              >
                {text}
              </ListTag>
              {!!get(subList, 'length') && (
                <AnimatedCollapse isOpen={isOpen} id={collapseId}>
                  <ul className={classnames('list-unstyled', subListClassName)}>
                    {subList.map(
                      (
                        { url: subUrl, text: subText, trackingId: subTrackingId, trackingValue: subTrackingValue },
                        subIndex
                      ) => {
                        const subListKey = `${keyPrefix}_sub-list-item_${subIndex}`;

                        return (
                          <li key={subListKey} className={subItemClassName}>
                            <a
                              href={subUrl}
                              className={classnames('list-link', linkClassName)}
                              data-tracking-id={subTrackingId}
                              data-tracking-value={subTrackingValue}
                            >
                              {subText}
                            </a>
                          </li>
                        );
                      }
                    )}
                  </ul>
                </AnimatedCollapse>
              )}
            </li>
          );
        })}
      </ul>
    </Fragment>
  );
}

LinksList.propTypes = {
  list: PropTypes.arrayOf(
    PropTypes.shape({
      url: PropTypes.string,
      text: PropTypes.oneOfType([PropTypes.node, PropTypes.string, PropTypes.arrayOf(PropTypes.node)]),
      highlighted: PropTypes.bool,
      trackingId: PropTypes.string,
      trackingValue: PropTypes.string,
    })
  ).isRequired,
  keyPrefix: PropTypes.string.isRequired,
  heading: PropTypes.string,
  headingClassName: PropTypes.string,
  listClassName: PropTypes.string,
  itemClassName: PropTypes.string,
  linkClassName: PropTypes.string,
  subListClassName: PropTypes.string,
  subItemClassName: PropTypes.string,
};
